import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  AdjustmentsHorizontalIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  BeakerIcon,
  BuildingLibraryIcon,
  CalendarIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ChevronDownIcon,
  ClockIcon,
  ComputerDesktopIcon,
  CurrencyDollarIcon,
  CursorArrowRaysIcon,
  DocumentArrowDownIcon,
  RocketLaunchIcon,
  ScaleIcon,
  SparklesIcon,
  TableCellsIcon,
  UserCircleIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

const BotPerformance = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30)),
  );
  const [endDate, setEndDate] = useState(new Date());
  const [botType, setBotType] = useState("all");
  const [performanceData, setPerformanceData] = useState(null);
  const [backtestHistory, setBacktestHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  // Utility function to export data to Excel
  const exportToExcel = (data, fileName) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Backtest History");

    // Generate buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    // Save file
    saveAs(dataBlob, `${fileName}.xlsx`);
  };

  // Utility function to export single backtest detail with orders
  const exportSingleBacktestDetail = (backtestId) => {
    // Find the backtest from the history
    const backtest = backtestHistory.find((item) => item.id === backtestId);
    if (!backtest) return;

    // In a real application, you would fetch the detailed orders for this backtest from API
    // For now, we'll generate mock order data
    const mockOrders = generateMockOrdersForBacktest(backtest);

    // Create workbook with multiple sheets
    const workbook = XLSX.utils.book_new();

    // Performance summary sheet
    const summaryData = [
      {
        "Bot Name": backtest.botName,
        Period: `${backtest.startDate} to ${backtest.endDate}`,
        "Initial Capital": `$${backtest.initialCapital.toLocaleString()}`,
        "Final Capital": `$${backtest.finalCapital.toLocaleString()}`,
        "Total Profit/Loss": `$${(
          backtest.finalCapital - backtest.initialCapital
        ).toLocaleString()}`,
        "Total Trades": backtest.totalTrades,
        "Win Rate": `${backtest.winRate}%`,
        "Profit Factor": backtest.profitFactor,
        "Max Drawdown": `${backtest.maxDrawdown}%`,
        "Created At": backtest.createdAt,
      },
    ];

    // Add summary sheet
    const summarySheet = XLSX.utils.json_to_sheet(summaryData);
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Performance Summary");

    // Add orders sheet
    const ordersSheet = XLSX.utils.json_to_sheet(mockOrders);
    XLSX.utils.book_append_sheet(workbook, ordersSheet, "All Orders");

    // Generate buffer
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    // Save file
    saveAs(
      dataBlob,
      `Backtest_Detail_${backtestId}_${backtest.botName.replace(
        /\s+/g,
        "_",
      )}.xlsx`,
    );
  };

  // Generate mock orders for a backtest
  const generateMockOrdersForBacktest = (backtest) => {
    const orders = [];
    const pairOptions = [
      "BTC/USDT",
      "ETH/USDT",
      "BNB/USDT",
      "SOL/USDT",
      "XRP/USDT",
    ];
    const typeOptions = ["market", "limit"];

    // Parse dates to generate realistic timestamps within the backtest period
    const startDate = new Date(backtest.startDate);
    const endDate = new Date(backtest.endDate);
    const timeRange = endDate.getTime() - startDate.getTime();

    // Generate order data based on total trades in the backtest
    let runningBalance = backtest.initialCapital;
    const totalTrades = parseInt(backtest.totalTrades);
    const winRate = parseFloat(backtest.winRate) / 100;

    for (let i = 0; i < totalTrades; i++) {
      // Randomize timestamp within the backtest period
      const randomTimeDelta = Math.floor(Math.random() * timeRange);
      const orderTime = new Date(startDate.getTime() + randomTimeDelta);
      const formattedTime = orderTime
        .toISOString()
        .replace("T", " ")
        .substring(0, 19);

      // Randomize pair
      const pair = pairOptions[Math.floor(Math.random() * pairOptions.length)];

      // Alternate between buy and sell
      const side = i % 2 === 0 ? "BUY" : "SELL";

      // Randomize price and amount
      const basePrice = pair.includes("BTC")
        ? 50000
        : pair.includes("ETH")
        ? 3000
        : 100;
      const price = (basePrice * (0.95 + Math.random() * 0.1)).toFixed(2);
      const amount = (0.01 + Math.random() * 0.2).toFixed(6);
      const total = (parseFloat(price) * parseFloat(amount)).toFixed(2);

      // Calculate fee (0.1%)
      const fee = (parseFloat(total) * 0.001).toFixed(2);

      // Calculate P/L for sell orders
      let pl = null;
      if (side === "SELL") {
        // Determine if this trade was a win based on win rate
        const isWin = Math.random() < winRate;
        pl = isWin
          ? (parseFloat(total) * (0.01 + Math.random() * 0.05)).toFixed(2)
          : (-parseFloat(total) * (0.01 + Math.random() * 0.03)).toFixed(2);

        // Update running balance
        runningBalance += parseFloat(pl);
      }

      orders.push({
        "Order ID": `ORD-${backtest.id}-${i + 1}`,
        "Date/Time": formattedTime,
        Exchange: "Binance",
        Pair: pair,
        Side: side,
        Type: typeOptions[Math.floor(Math.random() * typeOptions.length)],
        Price: `$${price}`,
        Amount: amount,
        Total: `$${total}`,
        Fee: `$${fee}`,
        "P/L": pl ? `$${pl}` : "-",
        Balance: `$${runningBalance.toFixed(2)}`,
      });
    }

    return orders;
  };

  // Utility function to prepare data for Excel export
  const prepareDataForExport = (data) => {
    return data.map((item) => ({
      ID: item.id,
      "Bot Name": item.botName,
      "Start Date": item.startDate,
      "End Date": item.endDate,
      "Initial Capital": `$${item.initialCapital.toLocaleString()}`,
      "Final Capital": `$${item.finalCapital.toLocaleString()}`,
      "Total Trades": item.totalTrades,
      "Win Rate": `${item.winRate}%`,
      "Profit Factor": item.profitFactor,
      "Max Drawdown": `${item.maxDrawdown}%`,
      "Created At": item.createdAt,
    }));
  };

  // Mock data
  useEffect(() => {
    setIsLoading(true);
    // Simulating API call with setTimeout
    setTimeout(() => {
      const mockData = {
        overview: {
          totalBots: 15,
          activeBots: 8,
          pausedBots: 5,
          stoppedBots: 2,
          totalSubscriptions: 23.47,
          averageWinRate: 68.5,
          maxDrawdown: 12.3,
          totalTrades: 34582,
          successfulTrades: 23689,
          backtestHistory: [
            {
              id: "BT-001",
              botName: "XenoBot Alpha",
              startDate: "2023-10-01",
              endDate: "2023-10-31",
              initialCapital: 1000,
              finalCapital: 1156.32,
              totalTrades: 24,
              winRate: "71.2",
              profitFactor: "2.14",
              maxDrawdown: "3.8",
              createdAt: "2023-11-01 09:15:22",
            },
            {
              id: "BT-002",
              botName: "GridTrader Pro",
              startDate: "2023-09-15",
              endDate: "2023-10-15",
              initialCapital: 5000,
              finalCapital: 5438.91,
              totalTrades: 36,
              winRate: "68.3",
              profitFactor: "1.89",
              maxDrawdown: "4.2",
              createdAt: "2023-10-16 14:30:45",
            },
            {
              id: "BT-003",
              botName: "CryptoScalper",
              startDate: "2023-11-01",
              endDate: "2023-11-15",
              initialCapital: 2000,
              finalCapital: 1876.45,
              totalTrades: 52,
              winRate: "57.8",
              profitFactor: "0.94",
              maxDrawdown: "8.1",
              createdAt: "2023-11-16 10:22:18",
            },
            {
              id: "BT-004",
              botName: "XenoBot Delta",
              startDate: "2023-10-15",
              endDate: "2023-11-15",
              initialCapital: 3000,
              finalCapital: 3482.11,
              totalTrades: 43,
              winRate: "74.5",
              profitFactor: "2.36",
              maxDrawdown: "2.9",
              createdAt: "2023-11-16 17:05:33",
            },
            {
              id: "BT-005",
              botName: "MarketMaster",
              startDate: "2023-09-01",
              endDate: "2023-11-01",
              initialCapital: 10000,
              finalCapital: 12567.89,
              totalTrades: 87,
              winRate: "69.1",
              profitFactor: "2.07",
              maxDrawdown: "5.2",
              createdAt: "2023-11-02 08:45:12",
            },
          ],
          botsByType: [
            { name: "Trend Following", count: 243, percentage: 28.4 },
            { name: "Scalping", count: 198, percentage: 23.1 },
            { name: "Arbitrage", count: 154, percentage: 18.0 },
            { name: "Grid Trading", count: 132, percentage: 15.4 },
            { name: "Mean Reversion", count: 129, percentage: 15.1 },
          ],
          monthlyPerformance: [
            { month: "Jan", profit: 3.2, trades: 2854 },
            { month: "Feb", profit: 2.8, trades: 2432 },
            { month: "Mar", profit: 4.1, trades: 3012 },
            { month: "Apr", profit: 3.5, trades: 2876 },
            { month: "May", profit: 2.9, trades: 2543 },
            { month: "Jun", profit: 3.8, trades: 3021 },
          ],
        },
        botProfiles: [
          {
            id: "bot1",
            name: "Alpha Trend Hunter",
            type: "Trend Following",
            createdAt: "2025-01-15",
            performance: {
              totalSubscriptions: 28.3,
              winRate: 72.1,
              maxDrawdown: 8.7,
              totalTrades: 1245,
              successfulTrades: 897,
            },
            users: 154,
          },
          {
            id: "bot2",
            name: "Swift Scalper Pro",
            type: "Scalping",
            createdAt: "2025-02-03",
            performance: {
              totalSubscriptions: 19.8,
              winRate: 65.3,
              maxDrawdown: 11.2,
              totalTrades: 3245,
              successfulTrades: 2119,
            },
            users: 128,
          },
          {
            id: "bot3",
            name: "ArbiMaster",
            type: "Arbitrage",
            createdAt: "2025-01-28",
            performance: {
              totalSubscriptions: 22.5,
              winRate: 78.9,
              maxDrawdown: 6.4,
              totalTrades: 876,
              successfulTrades: 691,
            },
            users: 112,
          },
          {
            id: "bot4",
            name: "Grid Master 2.0",
            type: "Grid Trading",
            createdAt: "2025-03-12",
            performance: {
              totalSubscriptions: 17.6,
              winRate: 64.2,
              maxDrawdown: 14.3,
              totalTrades: 1543,
              successfulTrades: 990,
            },
            users: 93,
          },
          {
            id: "bot5",
            name: "RevertX",
            type: "Mean Reversion",
            createdAt: "2025-02-18",
            performance: {
              totalSubscriptions: 21.4,
              winRate: 69.5,
              maxDrawdown: 9.8,
              totalTrades: 1087,
              successfulTrades: 756,
            },
            users: 87,
          },
        ],
        backtestResults: [
          {
            id: "backtest1",
            botId: "bot1",
            botName: "Alpha Trend Hunter",
            startDate: "2024-07-01",
            endDate: "2025-06-01",
            initialCapital: 10000,
            finalCapital: 12830,
            totalTrades: 534,
            winRate: 73.2,
            profitFactor: 2.4,
            maxDrawdown: 7.8,
          },
          {
            id: "backtest2",
            botId: "bot2",
            botName: "Swift Scalper Pro",
            startDate: "2024-07-01",
            endDate: "2025-06-01",
            initialCapital: 10000,
            finalCapital: 11980,
            totalTrades: 1287,
            winRate: 64.8,
            profitFactor: 1.9,
            maxDrawdown: 10.5,
          },
          {
            id: "backtest3",
            botId: "bot3",
            botName: "ArbiMaster",
            startDate: "2024-07-01",
            endDate: "2025-06-01",
            initialCapital: 10000,
            finalCapital: 12250,
            totalTrades: 387,
            winRate: 79.3,
            profitFactor: 3.1,
            maxDrawdown: 5.6,
          },
        ],
        backtestHistory: [
          {
            id: "hist001",
            botName: "Alpha Trend Hunter",
            startDate: "2024-07-01",
            endDate: "2024-09-01",
            initialCapital: 10000,
            finalCapital: 11245,
            totalTrades: 234,
            winRate: 71.3,
            profitFactor: 2.2,
            maxDrawdown: 6.3,
            createdAt: "2024-09-02",
          },
          {
            id: "hist002",
            botName: "Alpha Trend Hunter",
            startDate: "2024-09-05",
            endDate: "2024-12-05",
            initialCapital: 10000,
            finalCapital: 12267,
            totalTrades: 312,
            winRate: 73.8,
            profitFactor: 2.6,
            maxDrawdown: 5.4,
            createdAt: "2024-12-06",
          },
          {
            id: "hist003",
            botName: "Swift Scalper Pro",
            startDate: "2024-08-10",
            endDate: "2024-11-10",
            initialCapital: 10000,
            finalCapital: 10845,
            totalTrades: 876,
            winRate: 62.1,
            profitFactor: 1.7,
            maxDrawdown: 9.2,
            createdAt: "2024-11-11",
          },
          {
            id: "hist004",
            botName: "Grid Master 2.0",
            startDate: "2024-10-01",
            endDate: "2025-01-01",
            initialCapital: 10000,
            finalCapital: 11578,
            totalTrades: 432,
            winRate: 67.4,
            profitFactor: 2.1,
            maxDrawdown: 8.3,
            createdAt: "2025-01-02",
          },
          {
            id: "hist005",
            botName: "RevertX",
            startDate: "2024-11-15",
            endDate: "2025-02-15",
            initialCapital: 10000,
            finalCapital: 11921,
            totalTrades: 298,
            winRate: 70.5,
            profitFactor: 2.3,
            maxDrawdown: 7.2,
            createdAt: "2025-02-16",
          },
          {
            id: "hist006",
            botName: "ArbiMaster",
            startDate: "2024-12-01",
            endDate: "2025-03-01",
            initialCapital: 10000,
            finalCapital: 12115,
            totalTrades: 187,
            winRate: 78.1,
            profitFactor: 2.9,
            maxDrawdown: 4.8,
            createdAt: "2025-03-02",
          },
          {
            id: "hist007",
            botName: "Swift Scalper Pro",
            startDate: "2025-01-10",
            endDate: "2025-04-10",
            initialCapital: 10000,
            finalCapital: 11235,
            totalTrades: 942,
            winRate: 65.8,
            profitFactor: 1.8,
            maxDrawdown: 8.7,
            createdAt: "2025-04-11",
          },
        ],
      };

      setPerformanceData(mockData);
      setBacktestHistory(mockData.backtestHistory);
      setIsLoading(false);
    }, 500);
  }, [startDate, endDate, botType]);

  // Helper function for rendering metrics cards
  const renderMetricCard = (title, value, icon, change, positive) => {
    return (
      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
        <div className="flex justify-between items-center mb-2">
          <h4 className="text-sm font-medium text-gray-500">{title}</h4>
          {icon}
        </div>
        <div className="flex items-baseline">
          <p className="text-2xl font-semibold text-gray-900">{value}</p>
          <p
            className={`ml-2 text-sm font-medium ${
              positive ? "text-green-600" : "text-red-600"
            }`}
          >
            {positive ? "+" : ""}
            {change}%
          </p>
        </div>
      </div>
    );
  };

  // Tabs navigation
  const renderTabsNav = () => (
    <div className="border-b border-gray-200">
      <nav className="-mb-px flex space-x-8" aria-label="Tabs">
        <button
          onClick={() => setActiveTab("overview")}
          className={`${
            activeTab === "overview"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
        >
          <ChartBarIcon className="h-5 w-5 mr-2" />
          Overview
        </button>
        <button
          onClick={() => setActiveTab("botProfiles")}
          className={`${
            activeTab === "botProfiles"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
        >
          <RocketLaunchIcon className="h-5 w-5 mr-2" />
          Bot Performances
        </button>
        <button
          onClick={() => setActiveTab("backtestResults")}
          className={`${
            activeTab === "backtestResults"
              ? "border-blue-500 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
          } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
        >
          <BeakerIcon className="h-5 w-5 mr-2" />
          Backtest Performance
        </button>
      </nav>
    </div>
  );

  // Render filters section
  // GANTI SELURUH FUNGSI renderFilters DENGAN YANG INI
  const renderFilters = () => (
    <div className="flex flex-wrap gap-4 mb-6 items-center">
      <div>
        <label
          htmlFor="dateRange"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          <ClockIcon className="h-4 w-4 inline mr-1" />
          Date Range
        </label>
        <DatePicker
          selectsRange={true}
          startDate={startDate}
          endDate={endDate}
          onChange={(update) => {
            const [start, end] = update;
            setStartDate(start);
            setEndDate(end);
          }}
          // isClearable={true}
          dateFormat="yyyy/MM/dd"
          className="block w-full pl-3 pr-4 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
        />
      </div>
      <div>
        <label
          htmlFor="botType"
          className="block text-sm font-medium text-gray-700 mb-1"
        >
          <AdjustmentsHorizontalIcon className="h-4 w-4 inline mr-1" />
          Bot Type
        </label>
        <select
          id="botType"
          name="botType"
          value={botType}
          onChange={(e) => setBotType(e.target.value)}
          className="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
        >
          <option value="all">All Bots</option>
          <option value="trend">Trend Following</option>
          <option value="scalping">Scalping</option>
          <option value="arbitrage">Arbitrage</option>
          <option value="grid">Grid Trading</option>
          <option value="mean">Mean Reversion</option>
        </select>
      </div>
    </div>
  );

  // Render overview metrics dashboard
  const renderOverview = () => {
    if (!performanceData) return null;

    const { overview } = performanceData;

    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {renderMetricCard(
            "Total Profit",
            `${overview.totalSubscriptions}%`,
            <ArrowTrendingUpIcon className="h-5 w-5 text-blue-600" />,
            3.2,
            true,
          )}
          {renderMetricCard(
            "Win Rate",
            `${overview.averageWinRate}%`,
            <ScaleIcon className="h-5 w-5 text-blue-600" />,
            1.7,
            true,
          )}
          {renderMetricCard(
            "Max Drawdown",
            `${overview.maxDrawdown}%`,
            <ArrowTrendingDownIcon className="h-5 w-5 text-blue-600" />,
            0.5,
            false,
          )}
          {renderMetricCard(
            "Active Bots",
            overview.activeBots,
            <RocketLaunchIcon className="h-5 w-5 text-blue-600" />,
            5.3,
            true,
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Bot Type Distribution */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">
              Bot Type Distribution
            </h3>
            <div className="mt-4">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Bot Type
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Count
                    </th>
                    <th
                      scope="col"
                      className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                    >
                      Percentage
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {overview.botsByType.map((bot, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                        {bot.name}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {bot.count}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                        {bot.percentage}%
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Monthly Performance */}
          <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
            <h3 className="text-lg font-semibold mb-4">Monthly Performance</h3>
            <div className="mt-4">
              <div className="h-60 flex items-center justify-center">
                <p className="text-gray-500 italic">
                  Chart would be displayed here (using recharts or similar
                  library)
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Trading Summary</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
              <p className="text-sm text-gray-500 mb-1">Total Trades</p>
              <p className="text-xl font-semibold">
                {overview.totalTrades
                  ? overview.totalTrades.toLocaleString()
                  : "0"}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
              <p className="text-sm text-gray-500 mb-1">Successful Trades</p>
              <p className="text-xl font-semibold">
                {overview.successfulTrades
                  ? overview.successfulTrades.toLocaleString()
                  : "0"}
              </p>
            </div>
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
              <p className="text-sm text-gray-500 mb-1">Success Rate</p>
              <p className="text-xl font-semibold">
                {overview.successfulTrades && overview.totalTrades
                  ? (
                      (overview.successfulTrades / overview.totalTrades) *
                      100
                    ).toFixed(1)
                  : "0"}
                %
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render bot profiles
  const renderBotProfiles = () => {
    if (!performanceData) return null;

    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Bot Name
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Type
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Total Profit
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Win Rate
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Max Drawdown
                </th>
                <th
                  scope="col"
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                >
                  Users
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {performanceData.botProfiles.map((bot) => (
                <tr key={bot.id}>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {bot.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {bot.type}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="inline-flex items-center text-green-700">
                      <ArrowTrendingUpIcon className="h-4 w-4 mr-1" />
                      {bot.performance.totalSubscriptions}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {bot.performance.winRate}%
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <span className="text-red-600">
                      {bot.performance.maxDrawdown}%
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      <UserIcon className="h-4 w-4 mr-1 text-gray-400" />
                      {bot.users}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Handle Excel export action
  const handleExportHistory = () => {
    if (backtestHistory && backtestHistory.length > 0) {
      const dataToExport = prepareDataForExport(backtestHistory);
      exportToExcel(
        dataToExport,
        `Backtest_Performance_History_${new Date().toISOString().slice(0, 10)}`,
      );
    }
  };

  // Render backtest results
  const renderBacktestResults = () => {
    if (!performanceData) return null;

    // Mock trade history data
    const tradeHistory = [
      {
        id: "#1",
        exchange: "Binance",
        pair: "BTC/USDT",
        dateTime: "24-12-10 00:54:59",
        side: "BUY",
        type: "Market",
        price: "$27,671.93",
        amount: "0.00097072",
        total: "$26.90",
        fee: "$0.00000000",
        pl: "-",
      },
      {
        id: "#1",
        exchange: "Binance",
        pair: "BTC/USDT",
        dateTime: "24-12-10 01:05:53",
        side: "SELL",
        type: "Market",
        price: "$27,844.83",
        amount: "0.00097071",
        total: "$27.03",
        fee: "$0.00000000",
        pl: "$0.13",
      },
      {
        id: "#1",
        exchange: "Binance",
        pair: "BTC/USDT",
        dateTime: "24-12-10 10:59:59",
        side: "BUY",
        type: "Market",
        price: "$27,687.50",
        amount: "0.00100000",
        total: "$27.69",
        fee: "$0.00000000",
        pl: "$0.16",
      },
      {
        id: "#1",
        exchange: "Binance",
        pair: "BTC/USDT",
        dateTime: "24-12-10 16:58:59",
        side: "SELL",
        type: "Market",
        price: "$26,653.79",
        amount: "0.00104433",
        total: "$28.90",
        fee: "$0.00000000",
        pl: "-",
      },
      {
        id: "#1",
        exchange: "Binance",
        pair: "BTC/USDT",
        dateTime: "24-12-10 17:59:59",
        side: "BUY",
        type: "Market",
        price: "$26,484.01",
        amount: "0.00097672",
        total: "$25.90",
        fee: "$0.00000000",
        pl: "-",
      },
      {
        id: "#1",
        exchange: "Binance",
        pair: "BTC/USDT",
        dateTime: "24-12-10 20:59:29",
        side: "SELL",
        type: "Market",
        price: "$26,855.05",
        amount: "0.00100000",
        total: "$26.86",
        fee: "$0.00000000",
        pl: "$0.96",
      },
      {
        id: "#1",
        exchange: "Binance",
        pair: "BTC/USDT",
        dateTime: "24-12-10 21:59:59",
        side: "BUY",
        type: "Market",
        price: "$26,935.02",
        amount: "0.00100000",
        total: "$26.94",
        fee: "$0.00000000",
        pl: "$1.19",
      },
    ];

    return (
      <div className="space-y-6">
        {/* Common Settings Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6 hover:shadow-md transition-shadow duration-300">
          <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-800 border-b pb-2">
            <AdjustmentsHorizontalIcon className="h-5 w-5 mr-2 text-blue-600" />
            Common Settings
          </h3>

          <div className="bg-blue-50 bg-opacity-30 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Bot Strategy */}
                <div className="relative">
                  <label
                    htmlFor="botStrategy"
                    className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <BuildingLibraryIcon className="h-4 w-4 mr-2 text-blue-600" />
                    <span>Bot Strategy</span>
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <select
                      id="botStrategy"
                      name="botStrategy"
                      className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-white"
                      defaultValue="grid"
                    >
                      <option value="grid">Grid Trading</option>
                      <option value="dca">DCA Bot</option>
                      <option value="futures">Futures Bot</option>
                      <option value="smart">Smart Trade</option>
                    </select>
                  </div>
                </div>

                {/* Exchange */}
                <div className="relative">
                  <label
                    htmlFor="exchange"
                    className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <ComputerDesktopIcon className="h-4 w-4 mr-2 text-blue-600" />
                    <span>Exchange</span>
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <select
                      id="exchange"
                      name="exchange"
                      className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-white"
                      defaultValue="binance"
                    >
                      <option value="binance">Binance</option>
                      <option value="bybit">Bybit</option>
                      <option value="kucoin">KuCoin</option>
                      <option value="huobi">Huobi</option>
                    </select>
                  </div>
                </div>

                {/* Primary Pair */}
                <div className="relative">
                  <label
                    htmlFor="primaryPair"
                    className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <CurrencyDollarIcon className="h-4 w-4 mr-2 text-blue-600" />
                    <span>Primary Trading Pair</span>
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <select
                      id="primaryPair"
                      name="primaryPair"
                      className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-white"
                      defaultValue="btcusdt"
                    >
                      <option value="btcusdt">BTC/USDT</option>
                      <option value="ethusdt">ETH/USDT</option>
                      <option value="bnbusdt">BNB/USDT</option>
                      <option value="solusdt">SOL/USDT</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Timeframe */}
                <div className="relative">
                  <label
                    htmlFor="timeframe"
                    className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
                  >
                    <ClockIcon className="h-4 w-4 mr-2 text-blue-600" />
                    <span>Timeframe</span>
                  </label>
                  <div className="relative rounded-md shadow-sm">
                    <select
                      id="timeframe"
                      name="timeframe"
                      className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md bg-white"
                      defaultValue="1h"
                    >
                      <option value="15m">15m</option>
                      <option value="30m">30m</option>
                      <option value="1h">1h</option>
                      <option value="4h">4h</option>
                      <option value="1d">1d</option>
                    </select>
                  </div>
                </div>

                {/* Date Range Selector */}
                <div className="bg-white border border-gray-200 rounded-lg p-3 shadow-sm">
                  <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                    <ClockIcon className="h-4 w-4 mr-2 text-blue-600" />
                    <span>Backtest Period</span>
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label
                        htmlFor="startDate"
                        className="block text-xs font-medium text-gray-500 mb-1"
                      >
                        Start Date
                      </label>
                      <input
                        type="date"
                        id="startDate"
                        name="startDate"
                        defaultValue="2024-10-10"
                        className="block w-full pl-3 pr-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                      />
                    </div>

                    <div>
                      <label
                        htmlFor="endDate"
                        className="block text-xs font-medium text-gray-500 mb-1"
                      >
                        End Date
                      </label>
                      <input
                        type="date"
                        id="endDate"
                        name="endDate"
                        defaultValue="2024-12-12"
                        className="block w-full pl-3 pr-3 py-2 text-sm border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 rounded-md"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* XENO Settings Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mb-6 hover:shadow-md transition-shadow duration-300">
          <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-800 border-b pb-2">
            <BuildingLibraryIcon className="h-5 w-5 mr-2 text-blue-600" />
            XENO Settings (Grid Trading)
          </h3>

          <div className="bg-green-50 bg-opacity-30 p-4 rounded-lg mb-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Capital */}
              <div className="relative">
                <label
                  htmlFor="capital"
                  className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <CurrencyDollarIcon className="h-4 w-4 mr-2 text-blue-600" />
                  <span>Capital</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <input
                    type="number"
                    id="capital"
                    name="capital"
                    defaultValue="10000"
                    className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  />
                </div>
              </div>

              {/* Amount */}
              <div className="relative">
                <label
                  htmlFor="amount"
                  className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <SparklesIcon className="h-4 w-4 mr-2 text-blue-600" />
                  <span>Amount</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <input
                    type="number"
                    id="amount"
                    name="amount"
                    defaultValue="1"
                    className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  />
                </div>
              </div>

              {/* Grid Width */}
              <div className="relative">
                <label
                  htmlFor="gridWidth"
                  className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <ScaleIcon className="h-4 w-4 mr-2 text-blue-600" />
                  <span>Grid Width</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <input
                    type="number"
                    id="gridWidth"
                    name="gridWidth"
                    defaultValue="0.5"
                    className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  />
                </div>
              </div>

              {/* Number of Grids */}
              <div className="relative">
                <label
                  htmlFor="numberOfGrids"
                  className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <ChartBarIcon className="h-4 w-4 mr-2 text-blue-600" />
                  <span>Number of Grids</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <input
                    type="number"
                    id="numberOfGrids"
                    name="numberOfGrids"
                    defaultValue="10"
                    className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  />
                </div>
              </div>

              {/* Grid Width Exit */}
              <div className="relative">
                <label
                  htmlFor="gridWidthExit"
                  className="block text-sm font-medium text-gray-700 mb-2 flex items-center"
                >
                  <RocketLaunchIcon className="h-4 w-4 mr-2 text-blue-600" />
                  <span>Grid Width Exit</span>
                </label>
                <div className="relative rounded-md shadow-sm">
                  <input
                    type="number"
                    id="gridWidthExit"
                    name="gridWidthExit"
                    defaultValue="0.5"
                    className="block w-full pl-3 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="mt-6 flex justify-end pt-4 border-t border-gray-200">
            <button
              type="button"
              className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded-md flex items-center shadow-sm hover:shadow transition-all duration-300"
            >
              <BeakerIcon className="h-4 w-4 mr-1" />
              Start The Bot
            </button>
          </div>
        </div>

        {/* Backtest Results Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 hover:shadow-md transition-shadow duration-300">
          <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-800 border-b pb-2">
            <ChartBarIcon className="h-5 w-5 mr-2 text-blue-600" />
            Backtest Results
          </h3>

          <div className="bg-blue-50 bg-opacity-20 p-4 rounded-lg mb-6">
            <h4 className="text-base font-medium flex items-center mb-4">
              <ChartBarIcon className="h-4 w-4 mr-2 text-blue-600" />
              Performance Summary
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <p className="text-sm text-gray-500 mb-1 flex items-center">
                  <CurrencyDollarIcon className="h-3 w-3 mr-1 text-green-600" />
                  Grid Profit
                </p>
                <p className="text-xl font-semibold text-green-600">$8.90</p>
              </div>
              <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <p className="text-sm text-gray-500 mb-1 flex items-center">
                  <CurrencyDollarIcon className="h-3 w-3 mr-1 text-gray-600" />
                  Total Fees
                </p>
                <p className="text-xl font-semibold">$0.30</p>
              </div>
              <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <p className="text-sm text-gray-500 mb-1 flex items-center">
                  <ChartBarIcon className="h-3 w-3 mr-1 text-blue-600" />
                  Total Trades
                </p>
                <p className="text-xl font-semibold">7</p>
              </div>
              <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <p className="text-sm text-gray-500 mb-1 flex items-center">
                  <ArrowTrendingDownIcon className="h-3 w-3 mr-1 text-red-600" />
                  Maximum Drawdown
                </p>
                <p className="text-xl font-semibold text-red-600">1.7078%</p>
              </div>
              <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <p className="text-sm text-gray-500 mb-1 flex items-center">
                  <ArrowTrendingUpIcon className="h-3 w-3 mr-1 text-green-600" />
                  Closed Buys
                </p>
                <p className="text-xl font-semibold">7</p>
              </div>
              <div className="bg-white p-4 rounded-md border border-gray-200 shadow-sm hover:shadow-md transition-shadow duration-300">
                <p className="text-sm text-gray-500 mb-1 flex items-center">
                  <SparklesIcon className="h-3 w-3 mr-1 text-blue-600" />
                  Open Buys
                </p>
                <p className="text-xl font-semibold">2</p>
              </div>
            </div>
          </div>

          <h4 className="text-base font-medium flex items-center mb-4 border-b pb-2">
            <ClockIcon className="h-4 w-4 mr-2 text-blue-600" />
            Trade History
          </h4>
          <div className="overflow-x-auto rounded-lg shadow-sm border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-50">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    ID
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Exchange
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Pair
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Date/Time
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Side
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Type
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Price
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Amount
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Total
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Fee
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    P/L
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {tradeHistory.map((trade, index) => (
                  <tr key={index}>
                    <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                      {trade.id}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {trade.exchange}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {trade.pair}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {trade.dateTime}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      <span
                        className={`px-2 py-1 text-xs font-medium rounded-md ${
                          trade.side === "BUY"
                            ? "bg-green-100 text-green-800"
                            : "bg-red-100 text-red-800"
                        } shadow-sm flex items-center justify-center w-16`}
                      >
                        {trade.side === "BUY" ? (
                          <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                        ) : (
                          <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                        )}
                        {trade.side}
                      </span>
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {trade.type}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-900">
                      {trade.price}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {trade.amount}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {trade.total}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                      {trade.fee}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap text-sm">
                      {trade.pl && (
                        <span
                          className={`${
                            parseFloat(trade.pl) >= 0
                              ? "text-green-600"
                              : "text-red-600"
                          } font-medium flex items-center`}
                        >
                          {parseFloat(trade.pl) >= 0 ? (
                            <ArrowTrendingUpIcon className="h-3 w-3 mr-1" />
                          ) : (
                            <ArrowTrendingDownIcon className="h-3 w-3 mr-1" />
                          )}
                          {parseFloat(trade.pl) >= 0 ? "+" : ""}
                          {trade.pl}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              <p>Showing trades 1-10 of 50</p>
            </div>
            <div className="flex gap-2">
              <button className="px-3 py-1 border rounded-md bg-gray-100 hover:bg-gray-200 text-sm flex items-center text-gray-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Previous
              </button>
              <button className="px-3 py-1 border rounded-md bg-blue-600 hover:bg-blue-700 text-sm flex items-center text-white">
                Next
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Backtest Performance History Section */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 mt-6 hover:shadow-md transition-shadow duration-300">
          <div className="flex justify-between items-center mb-4 border-b pb-2">
            <h3 className="text-lg font-semibold flex items-center text-blue-800">
              <TableCellsIcon className="h-5 w-5 mr-2 text-blue-600" />
              Backtest Performance History
            </h3>
            <button
              onClick={handleExportHistory}
              className="px-3 py-1 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center text-sm"
            >
              <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
              Export to Excel
            </button>
          </div>

          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-blue-50">
                <tr>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    ID
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Bot Name
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    <div className="flex items-center">
                      <CalendarIcon className="h-3 w-3 mr-1" />
                      Period
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Initial Capital
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Final Capital
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Total Trades
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Win Rate
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Profit Factor
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Max Drawdown
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Created At
                  </th>
                  <th
                    scope="col"
                    className="px-3 py-3 text-left text-xs font-medium text-gray-600 uppercase tracking-wider"
                  >
                    Export
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {backtestHistory.map((history) => (
                  <tr key={history.id} className="hover:bg-gray-50">
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      {history.id}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium text-gray-900">
                      {history.botName}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      {history.startDate} - {history.endDate}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      ${history.initialCapital.toLocaleString()}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                      <span
                        className={
                          parseFloat(history.finalCapital) >=
                          parseFloat(history.initialCapital)
                            ? "text-green-600"
                            : "text-red-600"
                        }
                      >
                        ${history.finalCapital.toLocaleString()}
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      {history.totalTrades}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm">
                      <span className="flex items-center">
                        {parseFloat(history.winRate) >= 65 ? (
                          <ArrowTrendingUpIcon className="h-3 w-3 mr-1 text-green-600" />
                        ) : (
                          <ArrowTrendingDownIcon className="h-3 w-3 mr-1 text-red-600" />
                        )}
                        {history.winRate}%
                      </span>
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      {history.profitFactor}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-red-600">
                      {history.maxDrawdown}%
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      {history.createdAt}
                    </td>
                    <td className="px-3 py-3 whitespace-nowrap text-sm">
                      <button
                        onClick={() => exportSingleBacktestDetail(history.id)}
                        className="px-3 py-1 bg-blue-600 hover:bg-blue-700 text-white rounded-md flex items-center text-sm"
                      >
                        <DocumentArrowDownIcon className="h-4 w-4 mr-1" />
                        Export
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="text-sm text-gray-500">
              <p>Showing all {backtestHistory.length} history items</p>
            </div>
            <div className="flex gap-2">
              <button className="px-3 py-1 border rounded-md bg-gray-100 hover:bg-gray-200 text-sm flex items-center text-gray-700">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Previous
              </button>
              <button className="px-3 py-1 border rounded-md bg-blue-600 hover:bg-blue-700 text-sm flex items-center text-white">
                Next
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 ml-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 5l7 7-7 7"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 bg-gray-50 min-h-screen space-y-6">
      <div className="mb-8">
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
          Bot Performance
        </h1>
        <p className="text-gray-500">
          Analyze the performance metrics for trading bots
        </p>
      </div>

      {renderFilters()}
      {renderTabsNav()}

      <div className="mt-6">
        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
              <p className="mt-4 text-gray-500">Loading performance data...</p>
            </div>
          </div>
        ) : (
          <>
            {activeTab === "overview" && renderOverview()}
            {activeTab === "botProfiles" && renderBotProfiles()}
            {activeTab === "backtestResults" && renderBacktestResults()}
          </>
        )}
      </div>
    </div>
  );
};

export default BotPerformance;
